import { AiMode as EAiMode } from '@confinion/confideck-shared';
import type { MessageBarProps } from '@fluentui/react-components';

export { EAiMode };

export type TAiModeRequest = {
  aiMode: EAiMode;
};

export enum EAiAssistantOriginItemType {
  DOCUMENT = 'DOCUMENT',
  FOLDER = 'FOLDER',
  LITERATURE_MANAGEMENT = 'LITERATURE_MANAGEMENT',
  LITERATURE_ENTRY = 'LITERATURE_ENTRY',
}

export type TAiContentSelection = {
  text: string;
  elementIds: string[];
};

export type TAiAssistantOrigin = {
  item: string;
  itemType: EAiAssistantOriginItemType;
  contentSelection?: TAiContentSelection;
};

export type TAiAssistantSurface = 'sidebar' | 'overlay';

export type TAiAssistantTab = 'prompt' | 'answer';

export type TAiAssistantStatusMessage = {
  text: string;
  intent: Extract<MessageBarProps['intent'], 'error' | 'warning' | 'info'>;
};

// WARNING Mirrored in FE/BE - keep values and union branches in sync.
// Counterpart: backend/src/ai/useCases/runs/types/aiCitation.types.ts
export enum EAiCitationKind {
  KNOWLEDGE_CHUNK = 'KNOWLEDGE_CHUNK',
  ITEM_METADATA = 'ITEM_METADATA',
  LITERATURE_RECORD = 'LITERATURE_RECORD',
}

export enum EAiCitationSourceType {
  DOCUMENT = 'DOCUMENT',
  FILE = 'FILE',
  LITERATURE = 'LITERATURE',
}

type TAiCitationResponseBase = {
  source: string;
  sourceType: EAiCitationSourceType;
  sourceName?: string;
};

export type TAiKnowledgeChunkCitationResponse = TAiCitationResponseBase & {
  kind: EAiCitationKind.KNOWLEDGE_CHUNK;
  chunkId: string;
  quote?: string;
  elementId?: string;
};

export type TAiItemMetadataCitationResponse = TAiCitationResponseBase & {
  kind: EAiCitationKind.ITEM_METADATA;
  metadataLabels: string[];
};

export type TAiLiteratureRecordCitationResponse = TAiCitationResponseBase & {
  kind: EAiCitationKind.LITERATURE_RECORD;
  sourceType: EAiCitationSourceType.LITERATURE;
};

export type TAiCitationResponse =
  | TAiKnowledgeChunkCitationResponse
  | TAiItemMetadataCitationResponse
  | TAiLiteratureRecordCitationResponse;

// WARNING Mirrored in FE/BE - keep values in sync.
// Counterpart: backend/src/ai/useCases/runs/types/aiRunFeedback.types.ts
export enum EAiRunFeedbackSentiment {
  POSITIVE = 'positive',
  NEGATIVE = 'negative',
}

export enum EAiRunFeedbackReason {
  INCORRECT = 'incorrect',
  MISSING_SOURCE = 'missing-source',
  UNCLEAR = 'unclear',
  UNSAFE = 'unsafe',
  OTHER = 'other',
}

export type TAiRunFeedbackInput = {
  sentiment: EAiRunFeedbackSentiment;
  reasons?: EAiRunFeedbackReason[];
  comment?: string;
};

export type TAiRunFeedbackMutationArgs = { runId: string; feedback: TAiRunFeedbackInput };
